import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Home, BookOpen, ArrowLeft } from 'lucide-react';
import { Link } from 'wouter';
import { LectureDownloader } from '@/components/LectureDownloader';

export default function LifestyleInterventions() {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && currentSlide > 0) {
        setCurrentSlide(currentSlide - 1);
      } else if (event.key === 'ArrowRight' && currentSlide < slides.length - 1) {
        setCurrentSlide(currentSlide + 1);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentSlide]);

  const slides = [
    {
      title: "Lifestyle Interventions for Longevity & Health Optimization: A Clinical Primer",
      subtitle: "Bridging Evidence to Practice for Physicians",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-green-50 to-blue-50 p-8 rounded-lg text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Lifestyle Interventions for Longevity & Health Optimization
            </h1>
            <h2 className="text-2xl text-gray-700 mb-6">A Clinical Primer</h2>
            <p className="text-lg text-gray-600 mb-6">Bridging Evidence to Practice for Physicians</p>
            
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl mx-auto">
              <div className="space-y-3 text-gray-600">
                <p className="text-lg"><strong>Focus:</strong> Evidence-Based Lifestyle Medicine</p>
                <p className="text-lg"><strong>Scope:</strong> Nutrition, Exercise, Sleep & Circadian Health</p>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-xl font-semibold text-blue-900 mb-3">Clinical Application Framework</h3>
            <div className="space-y-2 text-sm text-blue-800">
              <p><strong>Evidence-Based:</strong> All recommendations grounded in peer-reviewed research</p>
              <p><strong>Practical:</strong> Actionable protocols for immediate clinical implementation</p>
              <p><strong>Comprehensive:</strong> Integrative approach to lifestyle medicine</p>
              <p><strong>Personalized:</strong> Tailored interventions based on individual patient needs</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "The Shift: From Lifespan to Healthspan",
      subtitle: "Longevity Medicine: Redefining Our Goals",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Key Definitions</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Lifespan:</strong> The number of years lived.</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Healthspan:</strong> The period of life spent in good health, free from chronic disease, maintaining high physical and cognitive function.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Goal of Longevity Medicine:</strong> Not just adding years to life, but life to those years. This involves the "compression of morbidity."</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p>Chronic diseases are major impediments to healthspan; lifestyle interventions are primary modalities for prevention and treatment, shifting from reactive disease-treatment to proactive health optimization.</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Lifespan vs. Healthspan Visualization</h3>
            <svg width="450" height="220" viewBox="0 0 450 220" xmlns="http://www.w3.org/2000/svg" className="mx-auto">
              <style>
                {`.label { font-family: Arial, sans-serif; font-size: 14px; fill: #333; }
                 .bar-text { font-family: Arial, sans-serif; font-size: 12px; fill: white; font-weight: bold;}
                 .axis-text { font-family: Arial, sans-serif; font-size: 10px; fill: #555; }`}
              </style>
              <text x="225" y="25" className="label" textAnchor="middle" fontWeight="bold">Lifespan vs. Healthspan</text>
              <rect x="50" y="60" width="350" height="30" fill="#B0BEC5" rx="5" ry="5"/>
              <text x="60" y="80" className="bar-text">Lifespan (Total Years Lived)</text>
              <text x="405" y="100" className="axis-text">Death</text>
              <line x1="400" y1="90" x2="400" y2="55" stroke="#555" strokeWidth="1"/>
              <rect x="50" y="110" width="250" height="30" fill="#4CAF50" rx="5" ry="5"/>
              <text x="60" y="130" className="bar-text">Healthspan (Healthy Years)</text>
              <rect x="300" y="110" width="100" height="30" fill="#FF7043" rx="5" ry="5"/>
              <text x="310" y="130" className="bar-text">Morbidity</text>
              <text x="50" y="150" className="axis-text">Birth</text>
              <line x1="50" y1="140" x2="50" y2="55" stroke="#555" strokeWidth="1"/>
              <path d="M 250 140 Q 275 155 300 140" fill="none" stroke="#0D47A1" strokeWidth="2" markerEnd="url(#arrowBlue)"/>
              <text x="275" y="175" className="label" textAnchor="middle" fill="#0D47A1" fontSize="12px">Goal: Compress Morbidity,</text>
              <text x="275" y="190" className="label" textAnchor="middle" fill="#0D47A1" fontSize="12px">Extend Healthspan</text>
              <defs>
                <marker id="arrowBlue" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#0D47A1"/>
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      )
    },
    {
      title: "Foundations: Functional & Lifestyle Medicine",
      subtitle: "Guiding Principles for Health Optimization",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Core Principles</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Functional Medicine:</strong> A systems-biology approach identifying root causes of dysfunction.</p>
                <p className="mt-2">Focuses on how sleep, nutrition, exercise, stress, and relationships interact.</p>
                <p className="mt-2">"Whole person" approach: considers genetics, environment, and lifestyle.</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Lifestyle Medicine:</strong> Evidence-based, prescriptive lifestyle changes to prevent, treat, and even reverse chronic conditions.</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">The Six Pillars of Lifestyle Medicine</h3>
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-800 mb-6">
              <div className="bg-green-50 p-3 rounded">1. A Whole-Food, Predominantly Plant-Based Diet</div>
              <div className="bg-blue-50 p-3 rounded">2. Regular Physical Activity</div>
              <div className="bg-yellow-50 p-3 rounded">3. Restorative Sleep</div>
              <div className="bg-orange-50 p-3 rounded">4. Stress Management</div>
              <div className="bg-red-50 p-3 rounded">5. Avoidance of Risky Substances</div>
              <div className="bg-purple-50 p-3 rounded">6. Positive Social Connections</div>
            </div>

            <svg width="450" height="250" viewBox="0 0 450 250" xmlns="http://www.w3.org/2000/svg" className="mx-auto">
              <style>
                {`.pillar-text { font-family: Arial, sans-serif; font-size: 11px; text-anchor: middle; fill: #333; }
                 .center-text { font-family: Arial, sans-serif; font-size: 16px; text-anchor: middle; fill: #004D40; font-weight: bold;}`}
              </style>
              <circle cx="225" cy="125" r="70" fill="#E0F2F1" stroke="#00796B" strokeWidth="2"/>
              <text x="225" y="120" className="center-text">Healthspan</text>
              <text x="225" y="140" className="center-text">Optimization</text>
              <g transform="translate(225,125)">
                <circle cx="0" cy="-95" r="25" fill="#81C784"/>
                <text x="0" y="-98" className="pillar-text">Whole-Food</text>
                <text x="0" y="-86" className="pillar-text">Nutrition</text>
                <circle cx="82" cy="-47.5" r="25" fill="#64B5F6"/>
                <text x="82" y="-50.5" className="pillar-text">Physical</text>
                <text x="82" y="-38.5" className="pillar-text">Activity</text>
                <circle cx="82" cy="47.5" r="25" fill="#FFF176"/>
                <text x="82" y="44.5" className="pillar-text">Restorative</text>
                <text x="82" y="56.5" className="pillar-text">Sleep</text>
                <circle cx="0" cy="95" r="25" fill="#FFB74D"/>
                <text x="0" y="92" className="pillar-text">Stress</text>
                <text x="0" y="104" className="pillar-text">Management</text>
                <circle cx="-82" cy="47.5" r="25" fill="#E57373"/>
                <text x="-82" y="44.5" className="pillar-text">Avoid Risky</text>
                <text x="-82" y="56.5" className="pillar-text">Substances</text>
                <circle cx="-82" cy="-47.5" r="25" fill="#BA68C8"/>
                <text x="-82" y="-50.5" className="pillar-text">Social</text>
                <text x="-82" y="-38.5" className="pillar-text">Connections</text>
              </g>
            </svg>
            
            <p className="text-sm text-gray-600 text-center mt-4">These pillars are interconnected; improvements in one often positively impact others.</p>
          </div>
        </div>
      )
    },
    {
      title: "The Evidence: Why Lifestyle Matters",
      subtitle: "The Power of Lifestyle: Evidence & Imperative",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Chronic Disease Burden in the US</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>5 leading causes of death</strong> may be preventable with lifestyle changes.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>42% of the population</strong> contends with 2+ chronic conditions.</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Approximately 90% of healthcare expenditures</strong> are for managing these chronic diseases.</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Lifestyle Medicine Efficacy</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-green-50 p-4 rounded">
                <p><strong>"Evidence-based, whole person, prescriptive lifestyle changes"</strong> can prevent, treat, and even reverse conditions like cardiovascular disease, type 2 diabetes, and obesity.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Public Health Impact:</strong> Broader adoption of lifestyle medicine principles could catalyze significant improvements in public health outcomes and lead to substantial reductions in healthcare costs.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Key Takeaway for Physicians:</strong> Lifestyle interventions are not merely "adjunctive" – they are foundational and potent therapeutic tools.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Nutritional Interventions: Fueling Longevity",
      subtitle: "Nutrition: The Cornerstone of Cellular Health",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Foundational Principle</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-green-50 p-4 rounded">
                <p><strong>A whole-food, predominantly plant-based diet</strong> is emphasized.</p>
                <p className="mt-2">Rich in fiber, essential micronutrients, phytonutrients, and antioxidants.</p>
                <p className="mt-2">Supports immunity, resilience to stressors, and overall wellness.</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Macronutrients & Cellular Health</h3>
            <div className="space-y-4 text-sm text-gray-800 mb-6">
              <div className="bg-blue-50 p-4 rounded">
                <p>Dietary choices directly fuel mitochondrial energy production (ATP).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p>Influence Reactive Oxygen Species (ROS) production and redox balance. ROS, in controlled amounts, are crucial signaling molecules.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p>Maintaining redox balance (e.g., via glutathione systems, thioredoxins) is vital for normal cellular function and modulating mitochondrial activity.</p>
              </div>
            </div>

            <h4 className="text-lg font-semibold text-gray-900 mb-4">Diet, Mitochondria, and Cellular Balance</h4>
            <svg width="450" height="200" viewBox="0 0 450 200" xmlns="http://www.w3.org/2000/svg" className="mx-auto">
              <style>{`.txt { font-family: Arial, sans-serif; font-size: 12px; text-anchor: middle; }`}</style>
              <rect x="20" y="80" width="100" height="40" rx="5" fill="#A5D6A7"/>
              <text x="70" y="105" className="txt">Whole Foods</text>
              <path d="M120 100 L170 100" stroke="#333" strokeWidth="2" markerEnd="url(#arrow)"/>
              <ellipse cx="230" cy="100" rx="60" ry="35" fill="#FFCC80" stroke="#FB8C00" strokeWidth="1.5"/>
              <text x="230" y="105" className="txt" fill="#A65100">Mitochondrion</text>
              <path d="M290 85 L340 65" stroke="#333" strokeWidth="2" markerEnd="url(#arrow)"/>
              <rect x="340" y="45" width="90" height="30" rx="5" fill="#81D4FA"/>
              <text x="385" y="65" className="txt">ATP (Energy)</text>
              <path d="M290 115 L340 135" stroke="#333" strokeWidth="2" markerEnd="url(#arrow)"/>
              <rect x="340" y="125" width="90" height="30" rx="5" fill="#EF9A9A"/>
              <text x="385" y="145" className="txt">ROS (Signaling)</text>
              <text x="230" y="165" className="txt" fill="#00796B">Optimal Diet → Healthy Mitochondria → Redox Balance → Healthspan</text>
              <defs><marker id="arrow" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><polygon points="0 0, 8 3, 0 6" fill="#333"/></marker></defs>
            </svg>
          </div>
        </div>
      )
    },
    {
      title: "Chrononutrition: The 'When' of Eating",
      subtitle: "Aligning Meals with Circadian Rhythms",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Core Concepts</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p>Meal timing is a crucial synchronizer of central (brain) and peripheral (organ) clocks, essential for metabolic health.</p>
              </div>
              <div className="bg-red-50 p-4 rounded">
                <p>Mistimed eating (e.g., late meals, frequent night consumption) can lead to circadian misalignment and increased risk for obesity, T2D, CVD.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Hormonal Influence:</strong> Morning cortisol (activates energy, appetite); evening melatonin (high levels + eating can impair glucose tolerance).</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Early Time-Restricted Eating (eTRE)</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Definition:</strong> Consuming daily calories within an earlier, limited window (e.g., 6-8 hours).</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Evidence:</strong> Improves insulin sensitivity, 24-hr glucose levels, BP, lipids; may affect clock genes, aging markers, and autophagy, even without caloric restriction or weight loss.</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Clinical Application: eTRE Protocol</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Objective:</strong> Align caloric intake with peak metabolic efficiency.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Feeding Window:</strong> Typically 6-8 hours, last meal mid-afternoon/early evening (e.g., 8 AM - 2 PM or 8 AM - 4 PM).</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Meal Distribution:</strong> Consider larger breakfast/lunch.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Monitoring (Baseline & 3-6 months):</strong></p>
                <p className="mt-2"><strong>Subjective:</strong> Energy, appetite, well-being.</p>
                <p className="mt-2"><strong>Biomarkers:</strong> Fasting glucose, fasting insulin, HbA1c, HOMA-IR, lipid panel, BP. Consider morning/evening cortisol.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Specific Dietary Approaches: Ketogenic Diet (KD)",
      subtitle: "Metabolic Flexibility & Benefits",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Definition & Mechanisms</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Definition:</strong> Very low carbohydrate (less than 50g/day or 5-10% energy), adequate protein, high fat. Induces nutritional ketosis (BHB as fuel).</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Mechanisms & Potential Benefits:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Appetite regulation (↓ghrelin)</li>
                  <li>Improved glycemic control (↓glucose, ↓insulin, ↓HbA1c)</li>
                  <li>Enhanced insulin sensitivity</li>
                  <li>Reduced inflammation (BHB may inhibit NLRP3 inflammasome)</li>
                </ul>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Crucial:</strong> Well-Formulated KD (WFKD): Emphasize whole, unprocessed foods. Avoid processed "keto" products.</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Clinical Application: WFKD Protocol</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Macronutrients:</strong> ~70-80% fat, 15-25% protein, 5-10% carbs.</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Food Selection:</strong> Healthy fats (MUFAs, omega-3s), quality protein, low-glycemic non-starchy vegetables.</p>
              </div>
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Electrolyte Management (Especially initial phase):</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Sodium (3-5g)</li>
                  <li>Potassium (3-4g from food/supplements)</li>
                  <li>Magnesium (300-500mg from food/supplements)</li>
                </ul>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Monitoring:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Blood ketones (BHB target: 0.5 - 3.0 mmol/L)</li>
                  <li>Blood glucose</li>
                  <li>Lipid panel (HDL, TG often improve; LDL-C can increase in some – "lean mass hyper-responders" – requires individualized assessment)</li>
                  <li>Kidney function (if pre-existing concerns)</li>
                </ul>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Considerations:</strong> "Keto-flu," risk of nutrient deficiencies if not well-formulated, contraindications (e.g., certain metabolic disorders).</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Specific Dietary Approaches: Omega-3 Fatty Acids",
      subtitle: "Cellular Integrity & Anti-Inflammation",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Core Mechanisms</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p>Essential PUFAs (EPA & DHA) are integral to cell membranes and influence fluidity, receptor function, and cell signaling.</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p>Well-recognized for combating chronic inflammation.</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Evidence for Longevity & Healthspan</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-purple-50 p-4 rounded">
                <p>Higher Omega-3 Index (RBC % EPA+DHA) linked to significantly lower risk for all-cause mortality (13-18% for highest vs. lowest quintiles).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p>Each 1% increase in omega-3 levels may correlate with a 20% decrease in early death risk.</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p>Omega-3s (esp. with Vitamin D & exercise) can slow biological aging (measured by epigenetic clocks like PhenoAge, GrimAge2).</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Clinical Application: Omega-3 Optimization</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Target Omega-3 Index:</strong> Aim for greater than 8%. Levels less than 4% indicate higher risk.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Dietary Sources:</strong> Fatty fish (salmon, mackerel, herring, sardines) ≥2 (3-oz) servings/week</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Supplementation:</strong> If diet insufficient, 1-2 grams combined EPA+DHA daily is a common starting dose. Adjust based on Index. (Studies use 0.84g to 2.6g).</p>
                <p className="mt-2">Prefer triglyceride form. Ensure high-quality, third-party tested (purity, potency, low oxidation).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Monitoring:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Omega-3 Index (baseline, then 3-6 months after starting/adjusting supplementation, then annually)</li>
                  <li>Consider inflammatory markers (e.g., hs-CRP)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Nutritional Interventions: Summary Table",
      subtitle: "Key Nutritional Interventions for Longevity: At a Glance",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <svg width="650" height="300" viewBox="0 0 650 300" xmlns="http://www.w3.org/2000/svg" className="mx-auto">
              <style>
                {`.header { font-family: Arial, sans-serif; font-size: 12px; font-weight: bold; fill: #333; text-anchor: middle; }
                 .cell { font-family: Arial, sans-serif; font-size: 11px; fill: #555; text-anchor: middle; }
                 .cell-left { font-family: Arial, sans-serif; font-size: 11px; fill: #555; text-anchor: start; }
                 .line { stroke: #ccc; stroke-width: 1; }`}
              </style>
              <text x="85" y="30" className="header">Intervention</text>
              <text x="240" y="30" className="header">Longevity Mechanisms</text>
              <text x="415" y="30" className="header">Protocol Highlights</text>
              <text x="570" y="30" className="header">Key Monitoring</text>
              <line x1="10" y1="45" x2="640" y2="45" className="line"/>
              <text x="85" y="70" className="cell">Whole-Food, Plant-Predom.</text>
              <text x="240" y="65" className="cell-left" dy="0">↑Phytonutrients, fiber, antioxidants;</text>
              <text x="240" y="65" className="cell-left" dy="1.2em">supports gut; ↓inflammation.</text>
              <text x="415" y="65" className="cell-left" dy="0">Emphasize fruits, veg, legumes,</text>
              <text x="415" y="65" className="cell-left" dy="1.2em">grains, nuts, seeds. Min. processed.</text>
              <text x="570" y="65" className="cell-left" dy="0">Diet analysis, hs-CRP,</text>
              <text x="570" y="65" className="cell-left" dy="1.2em">micronutrient status.</text>
              <line x1="10" y1="90" x2="640" y2="90" className="line"/>
              <text x="85" y="115" className="cell">Early Time-Restricted Eating</text>
              <text x="240" y="110" className="cell-left" dy="0">Circadian alignment, ↑insulin sens.,</text>
              <text x="240" y="110" className="cell-left" dy="1.2em">↑autophagy, ↓oxid. stress.</text>
              <text x="415" y="110" className="cell-left" dy="0">6-8h feed window (e.g., 8am-2pm).</text>
              <text x="415" y="110" className="cell-left" dy="1.2em">Last meal early.</text>
              <text x="570" y="110" className="cell-left" dy="0">Fasting glucose/insulin, HbA1c,</text>
              <text x="570" y="110" className="cell-left" dy="1.2em">HOMA-IR, lipids, BP.</text>
              <line x1="10" y1="135" x2="640" y2="135" className="line"/>
              <text x="85" y="160" className="cell">Well-Formulated Keto Diet</text>
              <text x="240" y="155" className="cell-left" dy="0">Metabolic switch, ↑insulin sens.,</text>
              <text x="240" y="155" className="cell-left" dy="1.2em">↓inflammation (BHB).</text>
              <text x="415" y="155" className="cell-left" dy="0">less than 50g carbs, 70-80% fat. Whole</text>
              <text x="415" y="155" className="cell-left" dy="1.2em">foods. Electrolyte mgt.</text>
              <text x="570" y="155" className="cell-left" dy="0">Blood ketones (BHB 0.5-3.0),</text>
              <text x="570" y="155" className="cell-left" dy="1.2em">glucose, lipids (LDL).</text>
              <line x1="10" y1="180" x2="640" y2="180" className="line"/>
              <text x="85" y="205" className="cell">Omega-3 Fatty Acids</text>
              <text x="240" y="200" className="cell-left" dy="0">Anti-inflammatory, ↓all-cause</text>
              <text x="240" y="200" className="cell-left" dy="1.2em">mortality, slows bio. aging.</text>
              <text x="415" y="200" className="cell-left" dy="0">Target Omega-3 Index greater than 8%.</text>
              <text x="415" y="200" className="cell-left" dy="1.2em">1-2g EPA+DHA/day.</text>
              <text x="570" y="200" className="cell-left" dy="0">Omega-3 Index (q3-6mo),</text>
              <text x="570" y="200" className="cell-left" dy="1.2em">hs-CRP.</text>
            </svg>
          </div>
        </div>
      )
    },
    {
      title: "Exercise & Movement: Foundations of Physical Health",
      subtitle: "Exercise: The Universal Medicine",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Core Principle</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Exercise is medicine.</strong> It's a polypill with widespread benefits for virtually every physiological system.</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p>Regular physical activity is one of the most potent interventions for extending healthspan and compressing morbidity.</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Mechanisms of Action</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Mitochondrial Biogenesis:</strong> Exercise stimulates the creation of new mitochondria, improving cellular energy production.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Autophagy Enhancement:</strong> Physical activity promotes cellular cleanup and recycling processes.</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Anti-inflammatory Effects:</strong> Regular exercise reduces chronic inflammation markers.</p>
              </div>
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Hormetic Stress:</strong> Exercise creates beneficial stress that triggers adaptive responses.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Muscle Protein Synthesis:</strong> Resistance training stimulates muscle growth and maintenance.</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Exercise-Longevity Pathway Visualization</h3>
            <svg width="500" height="250" viewBox="0 0 500 250" xmlns="http://www.w3.org/2000/svg" className="mx-auto">
              <style>{`.pathway-text { font-family: Arial, sans-serif; font-size: 11px; text-anchor: middle; fill: #333; }`}</style>
              <rect x="50" y="30" width="80" height="40" rx="5" fill="#81C784"/>
              <text x="90" y="55" className="pathway-text">Exercise</text>
              <path d="M130 50 L170 50" stroke="#333" strokeWidth="2" markerEnd="url(#arrowEx)"/>
              <circle cx="200" cy="50" r="30" fill="#FFB74D"/>
              <text x="200" y="55" className="pathway-text">Cellular</text>
              <text x="200" y="65" className="pathway-text">Stress</text>
              <path d="M230 50 L270 30" stroke="#333" strokeWidth="2" markerEnd="url(#arrowEx)"/>
              <path d="M230 50 L270 50" stroke="#333" strokeWidth="2" markerEnd="url(#arrowEx)"/>
              <path d="M230 50 L270 70" stroke="#333" strokeWidth="2" markerEnd="url(#arrowEx)"/>
              <rect x="270" y="15" width="80" height="25" rx="3" fill="#E1BEE7"/>
              <text x="310" y="32" className="pathway-text">Mitochondrial</text>
              <text x="310" y="40" className="pathway-text">Biogenesis</text>
              <rect x="270" y="42" width="80" height="25" rx="3" fill="#FFCDD2"/>
              <text x="310" y="59" className="pathway-text">Autophagy</text>
              <rect x="270" y="70" width="80" height="25" rx="3" fill="#C8E6C9"/>
              <text x="310" y="87" className="pathway-text">Anti-inflammation</text>
              <path d="M350 27 L390 50" stroke="#333" strokeWidth="2" markerEnd="url(#arrowEx)"/>
              <path d="M350 54 L390 50" stroke="#333" strokeWidth="2" markerEnd="url(#arrowEx)"/>
              <path d="M350 82 L390 50" stroke="#333" strokeWidth="2" markerEnd="url(#arrowEx)"/>
              <circle cx="420" cy="50" r="30" fill="#64B5F6"/>
              <text x="420" y="50" className="pathway-text">Enhanced</text>
              <text x="420" y="60" className="pathway-text">Healthspan</text>
              <defs><marker id="arrowEx" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><polygon points="0 0, 8 3, 0 6" fill="#333"/></marker></defs>
            </svg>
          </div>
        </div>
      )
    },
    {
      title: "Exercise Prescription: The Four Pillars",
      subtitle: "Evidence-Based Exercise Framework",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">The Four Pillars of Exercise</h3>
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <h4 className="font-semibold text-red-900 mb-2">1. Cardiorespiratory Fitness</h4>
                <p><strong>Zone 2 Training:</strong> 70-80% max HR, conversational pace</p>
                <p className="mt-2"><strong>Frequency:</strong> 3-4x/week, 45-60 minutes</p>
                <p className="mt-2"><strong>Benefits:</strong> Mitochondrial efficiency, fat oxidation</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <h4 className="font-semibold text-orange-900 mb-2">2. High-Intensity Training</h4>
                <p><strong>VO2 Max Work:</strong> 90-95% max HR</p>
                <p className="mt-2"><strong>Frequency:</strong> 1-2x/week, 4-8 intervals</p>
                <p className="mt-2"><strong>Benefits:</strong> Cardiac output, anaerobic capacity</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <h4 className="font-semibold text-blue-900 mb-2">3. Strength Training</h4>
                <p><strong>Progressive Overload:</strong> 65-85% 1RM</p>
                <p className="mt-2"><strong>Frequency:</strong> 2-3x/week, compound movements</p>
                <p className="mt-2"><strong>Benefits:</strong> Muscle mass, bone density, metabolic health</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <h4 className="font-semibold text-green-900 mb-2">4. Stability & Mobility</h4>
                <p><strong>Functional Movement:</strong> Balance, coordination, flexibility</p>
                <p className="mt-2"><strong>Frequency:</strong> Daily, 10-20 minutes</p>
                <p className="mt-2"><strong>Benefits:</strong> Injury prevention, movement quality</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Weekly Exercise Template</h3>
            <div className="space-y-3 text-sm text-gray-800">
              <div className="bg-blue-50 p-3 rounded">
                <p><strong>Monday:</strong> Strength Training (Upper Body) + Zone 2 (30 min)</p>
              </div>
              <div className="bg-green-50 p-3 rounded">
                <p><strong>Tuesday:</strong> Zone 2 Training (45-60 min) + Mobility</p>
              </div>
              <div className="bg-red-50 p-3 rounded">
                <p><strong>Wednesday:</strong> High-Intensity Intervals + Strength (Lower Body)</p>
              </div>
              <div className="bg-yellow-50 p-3 rounded">
                <p><strong>Thursday:</strong> Zone 2 Training (45-60 min) + Stability</p>
              </div>
              <div className="bg-purple-50 p-3 rounded">
                <p><strong>Friday:</strong> Strength Training (Full Body) + Mobility</p>
              </div>
              <div className="bg-orange-50 p-3 rounded">
                <p><strong>Weekend:</strong> Active recovery (walking, hiking, yoga) + Stability work</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Zone 2 Training: The Metabolic Foundation",
      subtitle: "Building Mitochondrial Efficiency",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Zone 2 Training Defined</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Definition:</strong> Exercise intensity where you can maintain a conversation while working at a steady, moderate effort.</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Physiological Markers:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>70-80% of maximum heart rate</li>
                  <li>Maximum fat oxidation rate</li>
                  <li>Highest sustainable aerobic power</li>
                  <li>Lactate threshold ~2mmol/L</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Metabolic Benefits</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Mitochondrial Adaptations:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Increased mitochondrial density</li>
                  <li>Enhanced oxidative enzyme activity</li>
                  <li>Improved fat oxidation capacity</li>
                  <li>Better oxygen utilization efficiency</li>
                </ul>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Metabolic Health:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Improved insulin sensitivity</li>
                  <li>Enhanced glucose regulation</li>
                  <li>Increased metabolic flexibility</li>
                  <li>Reduced oxidative stress</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Clinical Protocol: Zone 2 Implementation</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Frequency:</strong> 3-4 sessions per week</p>
              </div>
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Duration:</strong> 45-60 minutes per session</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Modalities:</strong> Walking, cycling, swimming, rowing, elliptical</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Progression:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Week 1-2: 30 minutes, 3x/week</li>
                  <li>Week 3-4: 40 minutes, 3x/week</li>
                  <li>Week 5+: 45-60 minutes, 3-4x/week</li>
                </ul>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Monitoring:</strong> Heart rate, perceived exertion (RPE 6-7/10), conversation test</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "High-Intensity Training: VO2 Max Optimization",
      subtitle: "Maximizing Cardiovascular Capacity",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">VO2 Max: The Gold Standard</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Definition:</strong> Maximum rate of oxygen consumption during intense exercise, reflecting cardiovascular fitness.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Longevity Significance:</strong> VO2 Max is one of the strongest predictors of all-cause mortality and healthspan.</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Age-Related Decline:</strong> Typically decreases 8-10% per decade after age 30 without intervention.</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">High-Intensity Interval Training (HIIT) Protocol</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Intensity:</strong> 90-95% maximum heart rate (RPE 9-10/10)</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Work Intervals:</strong> 3-8 minutes at high intensity</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Recovery:</strong> Equal or longer rest periods (3-8 minutes)</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Frequency:</strong> 1-2 sessions per week (recovery is crucial)</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Sample Protocols:</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>4 x 4 minutes @ 90-95% HRmax, 3 min recovery</li>
                  <li>6 x 3 minutes @ 90-95% HRmax, 3 min recovery</li>
                  <li>8 x 2 minutes @ 90-95% HRmax, 2 min recovery</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Physiological Adaptations</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Cardiac Adaptations:</strong> Increased stroke volume, cardiac output, and left ventricular mass</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Vascular Improvements:</strong> Enhanced capillary density and arterial compliance</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Metabolic Benefits:</strong> Improved lactate clearance and anaerobic capacity</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Strength Training: Preserving Lean Mass",
      subtitle: "Resistance Training for Longevity",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Sarcopenia: The Silent Threat</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Age-Related Muscle Loss:</strong> 3-8% per decade after age 30, accelerating after 60</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Consequences:</strong> Increased frailty, falls, metabolic dysfunction, mortality risk</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Reversibility:</strong> Resistance training can prevent and reverse age-related muscle loss</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Strength Training Principles</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Progressive Overload:</strong> Gradually increase weight, reps, or sets over time</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Compound Movements:</strong> Multi-joint exercises (squats, deadlifts, presses)</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Intensity:</strong> 65-85% of 1-rep maximum for muscle hypertrophy</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Volume:</strong> 10-20 sets per muscle group per week</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Frequency:</strong> 2-3 sessions per week with 48-72 hour recovery</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Essential Movement Patterns</h3>
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-800">
              <div className="bg-red-50 p-3 rounded">
                <h4 className="font-semibold mb-2">Upper Body Push</h4>
                <p>Push-ups, overhead press, bench press</p>
              </div>
              <div className="bg-blue-50 p-3 rounded">
                <h4 className="font-semibold mb-2">Upper Body Pull</h4>
                <p>Pull-ups, rows, lat pulldowns</p>
              </div>
              <div className="bg-green-50 p-3 rounded">
                <h4 className="font-semibold mb-2">Squat Pattern</h4>
                <p>Bodyweight squats, goblet squats, back squats</p>
              </div>
              <div className="bg-purple-50 p-3 rounded">
                <h4 className="font-semibold mb-2">Hip Hinge</h4>
                <p>Deadlifts, hip thrusts, Romanian deadlifts</p>
              </div>
              <div className="bg-orange-50 p-3 rounded">
                <h4 className="font-semibold mb-2">Unilateral</h4>
                <p>Lunges, single-leg deadlifts, step-ups</p>
              </div>
              <div className="bg-yellow-50 p-3 rounded">
                <h4 className="font-semibold mb-2">Core Stability</h4>
                <p>Planks, dead bugs, Pallof press</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Recovery & Movement: The Fourth Pillar",
      subtitle: "Stability, Mobility, and Recovery",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Movement Quality</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Stability:</strong> The ability to maintain or control joint position during movement</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Mobility:</strong> The ability to move joints through their full range of motion</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Balance:</strong> The ability to maintain equilibrium during static and dynamic activities</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Daily Movement Practice</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Morning Routine (5-10 minutes):</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Cat-cow stretches</li>
                  <li>Hip circles and leg swings</li>
                  <li>Shoulder rolls and arm circles</li>
                  <li>Deep breathing exercises</li>
                </ul>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Workday Breaks (2-3 minutes hourly):</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Desk stretches</li>
                  <li>Wall angels</li>
                  <li>Calf raises</li>
                  <li>Neck rotations</li>
                </ul>
              </div>
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Evening Routine (10-15 minutes):</strong></p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Hip flexor stretches</li>
                  <li>Thoracic spine mobility</li>
                  <li>Gentle yoga poses</li>
                  <li>Progressive muscle relaxation</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Recovery Strategies</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Sleep:</strong> 7-9 hours of quality sleep for muscle recovery and adaptation</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Nutrition:</strong> Adequate protein (1.6-2.2g/kg) and anti-inflammatory foods</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Hydration:</strong> Proper fluid intake for cellular function and recovery</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Stress Management:</strong> Mindfulness, meditation, and relaxation techniques</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Sleep: The Foundation of Health",
      subtitle: "Sleep Optimization for Longevity",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Sleep: Essential for Life</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Universal Need:</strong> All animals require sleep; it's a biological imperative for survival and optimal function.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Restorative Functions:</strong> Memory consolidation, cellular repair, toxin clearance, immune function, hormonal regulation.</p>
              </div>
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Consequences of Sleep Deprivation:</strong> Increased mortality risk, cognitive decline, metabolic dysfunction, weakened immunity.</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Sleep Architecture & Stages</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-green-50 p-4 rounded">
                <p><strong>NREM Stage 1:</strong> Light sleep, transition from wakefulness (5% of sleep)</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>NREM Stage 2:</strong> Deeper sleep, sleep spindles and K-complexes (45% of sleep)</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>NREM Stage 3:</strong> Deep sleep, slow-wave sleep, critical for restoration (25% of sleep)</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>REM Sleep:</strong> Rapid eye movement, dreaming, memory consolidation (25% of sleep)</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Sleep Cycle Visualization</h3>
            <svg width="500" height="200" viewBox="0 0 500 200" xmlns="http://www.w3.org/2000/svg" className="mx-auto">
              <style>{`.sleep-text { font-family: Arial, sans-serif; font-size: 10px; text-anchor: middle; fill: #333; }`}</style>
              <text x="250" y="20" className="sleep-text" fontSize="14px" fontWeight="bold">Sleep Cycle Progression (90-120 minutes)</text>
              <path d="M50 50 Q100 30 150 50 T250 50 T350 50 T450 50" stroke="#2196F3" strokeWidth="3" fill="none"/>
              <circle cx="75" cy="40" r="8" fill="#4CAF50"/>
              <text x="75" y="60" className="sleep-text">Stage 1</text>
              <circle cx="125" cy="35" r="8" fill="#FFC107"/>
              <text x="125" y="60" className="sleep-text">Stage 2</text>
              <circle cx="200" cy="30" r="8" fill="#FF9800"/>
              <text x="200" y="60" className="sleep-text">Stage 3</text>
              <text x="200" y="75" className="sleep-text">(Deep Sleep)</text>
              <circle cx="275" cy="40" r="8" fill="#E91E63"/>
              <text x="275" y="60" className="sleep-text">REM</text>
              <circle cx="350" cy="35" r="8" fill="#FF9800"/>
              <text x="350" y="60" className="sleep-text">Stage 3</text>
              <circle cx="425" cy="40" r="8" fill="#E91E63"/>
              <text x="425" y="60" className="sleep-text">REM</text>
              <text x="250" y="120" className="sleep-text" fontSize="12px">Multiple cycles occur throughout the night</text>
              <text x="250" y="140" className="sleep-text" fontSize="12px">Deep sleep dominates early cycles</text>
              <text x="250" y="155" className="sleep-text" fontSize="12px">REM sleep increases in later cycles</text>
            </svg>
          </div>
        </div>
      )
    },
    {
      title: "Circadian Biology: The Master Clock",
      subtitle: "Understanding Circadian Rhythms",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">The Circadian System</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Suprachiasmatic Nucleus (SCN):</strong> The master circadian clock in the brain, synchronized by light exposure.</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Peripheral Clocks:</strong> Nearly every cell contains molecular clocks that coordinate with the SCN.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Circadian Genes:</strong> CLOCK, BMAL1, PER, and CRY genes regulate 24-hour biological rhythms.</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Circadian Markers</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Cortisol:</strong> Peaks in early morning (6-8 AM), promoting wakefulness and energy</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Melatonin:</strong> Rises in evening darkness (9-10 PM), promoting sleepiness</p>
              </div>
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Core Body Temperature:</strong> Lowest around 4-6 AM, highest in late afternoon</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Growth Hormone:</strong> Peaks during deep sleep, essential for recovery and repair</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Circadian Rhythm Disruption</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Causes:</strong> Shift work, jet lag, irregular sleep schedules, late-night light exposure, late eating</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Health Consequences:</strong> Increased risk of diabetes, obesity, cardiovascular disease, cancer, mood disorders</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Metabolic Impact:</strong> Impaired glucose tolerance, insulin resistance, altered appetite hormones</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Sleep Hygiene: Practical Optimization",
      subtitle: "Evidence-Based Sleep Strategies",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Light Exposure Management</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Morning Light:</strong> 10-30 minutes of bright light within 30-60 minutes of waking</p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Natural sunlight preferred (10,000+ lux)</li>
                  <li>Light therapy boxes if sunlight unavailable</li>
                  <li>Helps set circadian rhythm and improve evening melatonin production</li>
                </ul>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Evening Light Reduction:</strong> Dim lights 2-3 hours before bedtime</p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Blue light blocking glasses or filters</li>
                  <li>Warm, dim lighting (less than 50 lux)</li>
                  <li>Blackout curtains or eye masks for sleeping</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Sleep Environment Optimization</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Temperature:</strong> Cool bedroom (65-68°F / 18-20°C) for optimal sleep quality</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Darkness:</strong> Complete darkness or minimal light exposure during sleep</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Quiet:</strong> Minimize noise or use white noise/earplugs</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Comfort:</strong> Quality mattress, pillows, and breathable bedding</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Behavioral Strategies</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Consistent Sleep Schedule:</strong> Same bedtime and wake time every day, including weekends</p>
              </div>
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Caffeine Timing:</strong> Avoid caffeine 8-10 hours before bedtime (6-hour half-life)</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Alcohol Limitation:</strong> Avoid alcohol 3-4 hours before bed (disrupts REM sleep)</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Exercise Timing:</strong> Regular exercise promotes sleep, but avoid vigorous activity 3-4 hours before bed</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Pre-Sleep Routine:</strong> Relaxing activities 30-60 minutes before bed (reading, meditation, gentle stretching)</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Sleep Tracking & Assessment",
      subtitle: "Monitoring Sleep Quality and Quantity",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Subjective Sleep Assessment</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Sleep Diary:</strong> Track bedtime, wake time, sleep latency, night awakenings, morning alertness</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Pittsburgh Sleep Quality Index (PSQI):</strong> Validated questionnaire assessing sleep quality over the past month</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Epworth Sleepiness Scale:</strong> Measures daytime sleepiness and fatigue levels</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Objective Sleep Monitoring</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Wearable Devices:</strong> Smartwatches and fitness trackers for sleep stage estimation</p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Heart rate variability (HRV)</li>
                  <li>Movement patterns</li>
                  <li>Sleep duration and efficiency</li>
                </ul>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Home Sleep Studies:</strong> More detailed analysis of sleep patterns</p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Respiratory events</li>
                  <li>Oxygen saturation</li>
                  <li>Position changes</li>
                </ul>
              </div>
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Polysomnography (PSG):</strong> Gold standard for sleep assessment in sleep clinics</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Sleep Metrics</h3>
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-3 rounded">
                <h4 className="font-semibold mb-2">Sleep Duration</h4>
                <p>Target: 7-9 hours for adults</p>
              </div>
              <div className="bg-green-50 p-3 rounded">
                <h4 className="font-semibold mb-2">Sleep Efficiency</h4>
                <p>Target: greater than 85% (time asleep/time in bed)</p>
              </div>
              <div className="bg-purple-50 p-3 rounded">
                <h4 className="font-semibold mb-2">Sleep Latency</h4>
                <p>Target: 10-20 minutes to fall asleep</p>
              </div>
              <div className="bg-orange-50 p-3 rounded">
                <h4 className="font-semibold mb-2">Wake Episodes</h4>
                <p>Target: fewer than 2 awakenings per night</p>
              </div>
              <div className="bg-yellow-50 p-3 rounded">
                <h4 className="font-semibold mb-2">Deep Sleep</h4>
                <p>Target: 15-20% of total sleep time</p>
              </div>
              <div className="bg-red-50 p-3 rounded">
                <h4 className="font-semibold mb-2">REM Sleep</h4>
                <p>Target: 20-25% of total sleep time</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Clinical Integration: Implementing Lifestyle Medicine",
      subtitle: "Practical Implementation in Clinical Practice",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Patient Assessment Framework</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Comprehensive History:</strong> Detailed lifestyle assessment including diet, exercise, sleep, stress, and social factors</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Validated Tools:</strong> Use standardized questionnaires (PSQI, food frequency questionnaires, exercise assessments)</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Biomarker Assessment:</strong> Relevant lab tests to establish baseline and track progress</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Readiness Assessment:</strong> Evaluate patient motivation and readiness for lifestyle changes</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Prescriptive Approach</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>SMART Goals:</strong> Specific, Measurable, Achievable, Relevant, Time-bound lifestyle prescriptions</p>
              </div>
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Gradual Implementation:</strong> Start with 1-2 changes, build momentum before adding more</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Written Prescriptions:</strong> Provide specific, written lifestyle prescriptions like medication orders</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Support Resources:</strong> Connect patients with nutrition counselors, exercise physiologists, sleep specialists</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Follow-up and Monitoring</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Regular Check-ins:</strong> Frequent initial follow-ups (2-4 weeks) to assess adherence and adjust plans</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Objective Measures:</strong> Track biomarkers, body composition, fitness metrics, sleep data</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Subjective Measures:</strong> Quality of life, energy levels, mood, perceived health status</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Course Corrections:</strong> Adjust interventions based on patient response and preferences</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "SHIFT Framework for Longevity Medicine",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-center">
            <h3 className="text-lg font-medium text-blue-800 mb-2">A signaling-based model for diagnosing, interpreting, and intervening in aging-related decline</h3>
            <p className="text-sm text-gray-700">Structured around five core biological functions</p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold mr-3">S</div>
                <h3 className="text-lg font-medium text-amber-800">Sensing</h3>
              </div>
              <p className="text-sm text-gray-700 mb-2"><strong>Definition:</strong> The body's ability to perceive internal and external cues (e.g., nutrients, stress, light, redox status).</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Pathways:</strong> mTOR, AMPK, insulin/IGF-1, sirtuins.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Example Biomarkers:</strong> Fasting insulin, HOMA-IR, leptin, glucose variability.</p>
              <p className="text-sm text-gray-700"><strong>Clinical Application:</strong> GLP-1 RAs, intermittent fasting, berberine, myo-inositol, CGMs.</p>
            </div>

            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold mr-3">H</div>
                <h3 className="text-lg font-medium text-green-800">Homeostatic Defense</h3>
              </div>
              <p className="text-sm text-gray-700 mb-2"><strong>Definition:</strong> The maintenance of equilibrium in systems like blood pressure, metabolism, and electrolytes under stress.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Pathways:</strong> Autophagy, Nrf2, unfolded protein response.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Example Biomarkers:</strong> hs-CRP, homocysteine, GGT, ferritin.</p>
              <p className="text-sm text-gray-700"><strong>Clinical Application:</strong> NAC, sauna, cold exposure, hormetic stressors.</p>
            </div>

            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white font-bold mr-3">I</div>
                <h3 className="text-lg font-medium text-red-800">Inflammation</h3>
              </div>
              <p className="text-sm text-gray-700 mb-2"><strong>Definition:</strong> Chronic low-grade inflammation that drives aging, immune dysregulation, and tissue dysfunction.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Pathways:</strong> NF-κB, inflammasome, COX/LOX, PGC-1α.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Example Biomarkers:</strong> hs-CRP, IL-6, TNF-α, omega-6/omega-3 ratio.</p>
              <p className="text-sm text-gray-700"><strong>Clinical Application:</strong> Omega-3s, curcumin, HRT, lifestyle medicine, targeted immunomodulation.</p>
            </div>

            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-3">F</div>
                <h3 className="text-lg font-medium text-purple-800">Fibrosis & Tissue Remodeling</h3>
              </div>
              <p className="text-sm text-gray-700 mb-2"><strong>Definition:</strong> The degradation or stiffening of tissue architecture via scarring, ECM breakdown, and cellular senescence.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Pathways:</strong> TGF-β, YAP/TAZ, MMPs.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Example Biomarkers:</strong> Fibroscan, elastography, GDF-15, collagen markers.</p>
              <p className="text-sm text-gray-700"><strong>Clinical Application:</strong> Rapamycin, senolytics, exosome therapy, regenerative peptides.</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold mr-3">T</div>
                <h3 className="text-lg font-medium text-blue-800">Timekeeping & Biorhythms</h3>
              </div>
              <p className="text-sm text-gray-700 mb-2"><strong>Definition:</strong> The cellular machinery responsible for circadian rhythm, repair, and regenerative capacity.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Pathways:</strong> CLOCK genes, melatonin signaling, telomerase, NAD+ synthesis.</p>
              <p className="text-sm text-gray-700 mb-2"><strong>Example Biomarkers:</strong> Cortisol awakening response, melatonin levels, biological age clocks.</p>
              <p className="text-sm text-gray-700"><strong>Clinical Application:</strong> Circadian entrainment, melatonin, chronological interventions, qEEG-driven neurotherapy.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Lifestyle Integration Strategies",
      subtitle: "Implementing SHIFT Framework Principles",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Nutrition Applications</h3>
              <div className="space-y-2 text-sm text-blue-800">
                <p><strong>Sensing Support:</strong> Time-restricted eating, nutrient timing</p>
                <p><strong>Homeostatic Defense:</strong> Antioxidant-rich foods, polyphenols</p>
                <p><strong>Anti-inflammatory:</strong> Mediterranean diet, omega-3s</p>
                <p><strong>Tissue Support:</strong> Collagen peptides, vitamin C</p>
                <p><strong>Circadian:</strong> Meal timing, light exposure coordination</p>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-green-900 mb-3">Exercise Applications</h3>
              <div className="space-y-2 text-sm text-green-800">
                <p><strong>Metabolic:</strong> Zone 2 training, strength training</p>
                <p><strong>Cellular Repair:</strong> Autophagy stimulation, mitophagy</p>
                <p><strong>Immune Support:</strong> Moderate exercise protocols</p>
                <p><strong>Structural:</strong> Resistance training, flexibility work</p>
                <p><strong>Timing:</strong> Circadian exercise scheduling</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Clinical Implementation Strategies",
      subtitle: "Practical Application in Healthcare Settings",
      content: (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Practice Integration</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Staff Training:</strong> Educate healthcare team on lifestyle medicine principles and motivational interviewing techniques</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Workflow Integration:</strong> Incorporate lifestyle assessments into routine visits and electronic health records</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Resource Development:</strong> Create patient handouts, prescription pads for lifestyle interventions, and referral networks</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Outcome Tracking:</strong> Implement systems to monitor patient progress and measure intervention effectiveness</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Patient Engagement Strategies</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Motivational Interviewing:</strong> Use open-ended questions, reflective listening, and collaborative goal-setting</p>
              </div>
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Behavioral Change Techniques:</strong> Implement habit stacking, environmental modifications, and social support systems</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Technology Integration:</strong> Leverage apps, wearables, and digital health tools for monitoring and motivation</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Group Programs:</strong> Develop group lifestyle medicine programs for peer support and cost-effectiveness</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Overcoming Common Barriers</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Time Constraints:</strong> Use brief interventions, delegate to trained staff, focus on high-impact changes</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Patient Resistance:</strong> Start small, emphasize benefits, address concerns, use patient-centered approach</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Insurance/Reimbursement:</strong> Document medical necessity, use appropriate codes, explore direct-pay options</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Provider Knowledge:</strong> Pursue lifestyle medicine training, join professional organizations, stay updated on research</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Conclusion: The Future of Medicine",
      subtitle: "Lifestyle Medicine as the Foundation of Healthcare",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-50 to-green-50 p-8 rounded-lg text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Paradigm Shift in Healthcare</h3>
            <p className="text-lg text-gray-700 mb-6">From treating disease to optimizing health</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Takeaways</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Evidence-Based Foundation:</strong> Lifestyle interventions are supported by robust scientific evidence and should be considered first-line treatments for many chronic conditions.</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Comprehensive Approach:</strong> Optimal health requires integration of nutrition, exercise, sleep, stress management, and social connections.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p><strong>Personalized Medicine:</strong> Lifestyle interventions should be tailored to individual needs, preferences, and circumstances for maximum effectiveness.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded">
                <p><strong>Preventive Focus:</strong> Shifting from reactive disease treatment to proactive health optimization can dramatically improve outcomes and reduce healthcare costs.</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Clinical Implementation</h3>
            <div className="space-y-4 text-sm text-gray-800">
              <div className="bg-yellow-50 p-4 rounded">
                <p><strong>Start Today:</strong> Begin incorporating lifestyle assessments and prescriptions into your practice immediately</p>
              </div>
              <div className="bg-red-50 p-4 rounded">
                <p><strong>Continuous Learning:</strong> Stay updated on lifestyle medicine research and best practices through ongoing education</p>
              </div>
              <div className="bg-blue-50 p-4 rounded">
                <p><strong>Patient Partnership:</strong> Work collaboratively with patients to develop sustainable lifestyle changes</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p><strong>Measure Outcomes:</strong> Track both objective biomarkers and subjective quality of life improvements</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">The Future is Lifestyle Medicine</h3>
            <p className="text-sm text-gray-700">
              By embracing lifestyle medicine, we can help our patients not just live longer, but live better—optimizing healthspan and compressing morbidity for generations to come.
            </p>
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Link href="/mentorship" className="inline-flex items-center text-blue-600 hover:text-blue-800">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Mentorship Program
            </Link>
            <LectureDownloader lectureName="Lifestyle Interventions" fileName="LifestyleInterventions" />
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                SHIFT Framework
              </span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Lifestyle Interventions for Longevity
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A SHIFT Framework Approach to Health Optimization
            </p>
          </div>
        </div>

        {/* Slide Counter */}
        <div className="text-center mb-6">
          <span className="text-sm text-gray-500">
            Slide {currentSlide + 1} of {slides.length}
          </span>
        </div>
      </div>

      {/* Navigation Bar */}
      <nav className="bg-white shadow-lg border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Breadcrumb */}
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Link href="/" className="hover:text-blue-600 transition-colors">
                <Home className="h-4 w-4" />
              </Link>
              <span>/</span>
              <Link href="/mentorship" className="hover:text-blue-600 transition-colors">
                Mentorship
              </Link>
              <span>/</span>
              <Link href="/mentorship" className="hover:text-blue-600 transition-colors flex items-center">
                <BookOpen className="h-4 w-4 mr-1" />
                Lectures
              </Link>
              <span>/</span>
              <span className="text-blue-600 font-medium">Lifestyle Interventions</span>
            </div>

            {/* Slide Counter */}
            <div className="text-sm text-gray-600">
              Slide {currentSlide + 1} of {slides.length}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Slide Content */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 min-h-[600px]">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {slides[currentSlide].title}
            </h1>
            {slides[currentSlide].subtitle && (
              <h2 className="text-xl text-gray-600">
                {slides[currentSlide].subtitle}
              </h2>
            )}
          </div>
          
          <div className="prose prose-lg max-w-none">
            {slides[currentSlide].content}
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="flex justify-between items-center">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="h-5 w-5 mr-2" />
            Previous
          </button>

          <div className="flex space-x-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentSlide ? 'bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Next
            <ChevronRight className="h-5 w-5 ml-2" />
          </button>
        </div>

        {/* Audio Lecture Section */}
        <div className="mt-12 bg-white rounded-xl shadow-lg p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Audio Lecture</h3>
          <div className="bg-blue-50 p-6 rounded-lg">
            <iframe
              src="https://share.descript.com/embed/M3JI6ZuLE5u"
              width="100%"
              height="360"
              frameBorder="0"
              allowFullScreen
              className="rounded-lg shadow-md"
              title="Lifestyle Interventions Audio Lecture"
            ></iframe>
          </div>
          <p className="text-sm text-gray-600 mt-4">
            Listen to the comprehensive audio lecture covering all aspects of lifestyle interventions for longevity and health optimization.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mt-8">
          <div className="bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
            ></div>
          </div>
          <p className="text-center text-sm text-gray-600 mt-2">
            Progress: {Math.round(((currentSlide + 1) / slides.length) * 100)}%
          </p>
        </div>
      </div>
    </div>
  );
}